import { useState, useEffect, useCallback, useRef } from 'react';
import { isNative } from '@/lib/platform';

// Notification permission states
export type NotificationPermission = 'default' | 'granted' | 'denied' | 'unsupported';

// Types of notifications we can show
export type DesktopNotificationType =
  | 'huddle_invite'
  | 'huddle_joined'
  | 'huddle_ended'
  | 'client_message'
  | 'team_message'
  | 'task_assigned'
  | 'mention';

// Notification action (for interactive notifications)
export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

// Options for showing a notification
export interface DesktopNotificationOptions {
  type: DesktopNotificationType;
  title: string;
  body: string;
  icon?: string;
  tag?: string;
  data?: Record<string, any>;
  actions?: NotificationAction[];
  requireInteraction?: boolean;
  silent?: boolean;
  url?: string;
  // For huddle notifications
  huddleId?: string;
}

// Sound effects for different notification types
const NOTIFICATION_SOUNDS: Record<DesktopNotificationType, string | null> = {
  huddle_invite: '/sounds/huddle-ring.mp3',
  huddle_joined: null, // No sound
  huddle_ended: null, // No sound
  client_message: '/sounds/message.mp3',
  team_message: '/sounds/message.mp3',
  task_assigned: '/sounds/alert.mp3',
  mention: '/sounds/mention.mp3',
};

// Preferences storage key
const PREFS_KEY = 'desktopNotificationPrefs';

export interface DesktopNotificationPrefs {
  enabled: boolean;
  soundEnabled: boolean;
  // Per-type preferences (when to show)
  huddles: 'always' | 'when_away' | 'never';
  clientMessages: 'always' | 'when_away' | 'never';
  teamMessages: 'always' | 'when_away' | 'never';
  // Quiet hours (24h format, e.g., "22:00" - "08:00")
  quietHoursEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
}

const DEFAULT_PREFS: DesktopNotificationPrefs = {
  enabled: true,
  soundEnabled: true,
  huddles: 'always',
  clientMessages: 'always',
  teamMessages: 'when_away',
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
};

interface DesktopNotificationState {
  permission: NotificationPermission;
  isSupported: boolean;
  serviceWorkerReady: boolean;
  preferences: DesktopNotificationPrefs;
}

export function useDesktopNotifications() {
  const [state, setState] = useState<DesktopNotificationState>({
    permission: 'default',
    isSupported: false,
    serviceWorkerReady: false,
    preferences: DEFAULT_PREFS,
  });

  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Check if we're in quiet hours
  const isQuietHours = useCallback((): boolean => {
    if (!state.preferences.quietHoursEnabled) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = state.preferences.quietHoursStart.split(':').map(Number);
    const [endHour, endMin] = state.preferences.quietHoursEnd.split(':').map(Number);

    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    // Handle overnight quiet hours (e.g., 22:00 - 08:00)
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime < endTime;
    }

    return currentTime >= startTime && currentTime < endTime;
  }, [state.preferences.quietHoursEnabled, state.preferences.quietHoursStart, state.preferences.quietHoursEnd]);

  // Check if notification should be shown based on preferences
  const shouldShowNotification = useCallback((type: DesktopNotificationType): boolean => {
    if (!state.preferences.enabled) return false;
    if (isQuietHours()) return false;
    if (state.permission !== 'granted') return false;

    // Get the preference for this type
    let pref: 'always' | 'when_away' | 'never' = 'always';
    if (type === 'huddle_invite' || type === 'huddle_joined' || type === 'huddle_ended') {
      pref = state.preferences.huddles;
    } else if (type === 'client_message') {
      pref = state.preferences.clientMessages;
    } else if (type === 'team_message' || type === 'mention') {
      pref = state.preferences.teamMessages;
    }

    if (pref === 'never') return false;
    if (pref === 'when_away' && !document.hidden) return false;

    return true;
  }, [state.preferences, state.permission, isQuietHours]);

  // Load preferences from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(PREFS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<DesktopNotificationPrefs>;
        setState(prev => ({
          ...prev,
          preferences: { ...DEFAULT_PREFS, ...parsed },
        }));
      }
    } catch (e) {
      // Ignore localStorage errors
    }
  }, []);

  // Check notification support and permission on mount
  useEffect(() => {
    // Check if notifications are supported (disabled on native - use NativePushProvider instead)
    const isSupported = !isNative && 'Notification' in window && 'serviceWorker' in navigator;

    if (!isSupported) {
      setState(prev => ({
        ...prev,
        isSupported: false,
        permission: 'unsupported',
      }));
      return;
    }

    // Get current permission
    const permission = Notification.permission as NotificationPermission;

    setState(prev => ({
      ...prev,
      isSupported: true,
      permission,
    }));

    // Register service worker
    registerServiceWorker();
  }, []);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!('serviceWorker' in navigator)) {
      console.warn('[Notifications] Service workers not supported');
      return;
    }

    try {
      // Register our service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      swRegistrationRef.current = registration;
      console.log('[Notifications] Service worker registered:', registration.scope);

      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;

      setState(prev => ({ ...prev, serviceWorkerReady: true }));

      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

      return registration;
    } catch (error) {
      console.error('[Notifications] Service worker registration failed:', error);
    }
  }, []);

  // Handle messages from service worker
  const handleServiceWorkerMessage = useCallback((event: MessageEvent) => {
    const { type, ...data } = event.data || {};

    switch (type) {
      case 'NOTIFICATION_CLICK':
        // Dispatch custom event for the app to handle
        window.dispatchEvent(new CustomEvent('desktop-notification-click', { detail: data }));
        break;

      case 'NOTIFICATION_DISMISSED':
        // Dispatch custom event for analytics or state updates
        window.dispatchEvent(new CustomEvent('desktop-notification-dismissed', { detail: data }));
        break;

      default:
        console.log('[Notifications] Unknown service worker message:', type);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!state.isSupported) {
      return 'unsupported';
    }

    try {
      const result = await Notification.requestPermission();
      const permission = result as NotificationPermission;

      setState(prev => ({ ...prev, permission }));

      return permission;
    } catch (error) {
      console.error('[Notifications] Permission request failed:', error);
      return 'denied';
    }
  }, [state.isSupported]);

  // Play notification sound
  const playSound = useCallback((type: DesktopNotificationType) => {
    if (!state.preferences.soundEnabled) return;
    if (isQuietHours()) return;

    const soundUrl = NOTIFICATION_SOUNDS[type];
    if (!soundUrl) return;

    try {
      // Reuse audio element for efficiency
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      audioRef.current.src = soundUrl;
      audioRef.current.volume = 0.5;
      audioRef.current.play().catch(e => {
        // Browser may block autoplay
        console.warn('[Notifications] Could not play sound:', e);
      });
    } catch (error) {
      console.warn('[Notifications] Error playing sound:', error);
    }
  }, [state.preferences.soundEnabled, isQuietHours]);

  // Show a desktop notification
  const showNotification = useCallback(async (options: DesktopNotificationOptions): Promise<boolean> => {
    // Check if we should show this notification
    if (!shouldShowNotification(options.type)) {
      return false;
    }

    const {
      type,
      title,
      body,
      icon = '/ic-logo.png',
      tag,
      data = {},
      actions = [],
      requireInteraction = false,
      silent = false,
      url,
      huddleId,
    } = options;

    // Play sound (unless silent)
    if (!silent) {
      playSound(type);
    }

    // Build notification data
    const notificationData = {
      ...data,
      type,
      url,
      huddleId,
      timestamp: Date.now(),
    };

    // Try to use service worker to show notification (works in background)
    if (state.serviceWorkerReady && swRegistrationRef.current) {
      try {
        await swRegistrationRef.current.showNotification(title, {
          body,
          icon,
          badge: '/ic-logo.png',
          tag: tag || `${type}-${Date.now()}`,
          data: notificationData,
          actions,
          requireInteraction,
          silent: true, // We handle sound separately
          vibrate: [200, 100, 200],
        });
        console.log('[Notifications] Notification shown via service worker');
        return true;
      } catch (error) {
        console.warn('[Notifications] Service worker notification failed, falling back:', error);
      }
    }

    // Fallback: Show notification directly (won't work in background)
    try {
      const notification = new Notification(title, {
        body,
        icon,
        tag: tag || `${type}-${Date.now()}`,
        data: notificationData,
        requireInteraction,
        silent: true,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        window.dispatchEvent(new CustomEvent('desktop-notification-click', {
          detail: { type, url, huddleId, ...data },
        }));
      };

      console.log('[Notifications] Notification shown via Notification API');
      return true;
    } catch (error) {
      console.error('[Notifications] Failed to show notification:', error);
      return false;
    }
  }, [shouldShowNotification, playSound, state.serviceWorkerReady]);

  // Convenience methods for specific notification types
  const showHuddleInvite = useCallback(async (
    initiatorName: string,
    huddleId: string,
    huddleType: 'audio' | 'video' = 'audio'
  ): Promise<boolean> => {
    return showNotification({
      type: 'huddle_invite',
      title: 'Incoming Huddle',
      body: `${initiatorName} is calling you`,
      tag: `huddle-invite-${huddleId}`,
      huddleId,
      requireInteraction: true, // Keep on screen until user responds
      actions: [
        { action: 'answer', title: 'Answer' },
        { action: 'decline', title: 'Decline' },
      ],
      data: { huddleType },
      url: `/?huddle=${huddleId}`,
    });
  }, [showNotification]);

  const showClientMessage = useCallback(async (
    clientName: string,
    senderName: string,
    messagePreview: string,
    channelId: string
  ): Promise<boolean> => {
    return showNotification({
      type: 'client_message',
      title: `${clientName}`,
      body: `${senderName}: ${messagePreview}`,
      tag: `client-msg-${channelId}`,
      url: `/clients?channel=${channelId}`,
    });
  }, [showNotification]);

  const showTeamMessage = useCallback(async (
    senderName: string,
    messagePreview: string,
    channelId: string
  ): Promise<boolean> => {
    return showNotification({
      type: 'team_message',
      title: 'Team Hub Message',
      body: `${senderName}: ${messagePreview}`,
      tag: `team-msg-${channelId}`,
      url: `/team?channel=${channelId}`,
    });
  }, [showNotification]);

  const showMention = useCallback(async (
    senderName: string,
    context: string,
    url: string
  ): Promise<boolean> => {
    return showNotification({
      type: 'mention',
      title: 'You were mentioned',
      body: `${senderName} mentioned you: ${context}`,
      url,
    });
  }, [showNotification]);

  const showTaskAssigned = useCallback(async (
    taskTitle: string,
    assignedBy: string,
    taskId: string
  ): Promise<boolean> => {
    return showNotification({
      type: 'task_assigned',
      title: 'Task Assigned',
      body: `${assignedBy} assigned you: ${taskTitle}`,
      url: `/tasks?id=${taskId}`,
    });
  }, [showNotification]);

  // Update preferences
  const updatePreferences = useCallback((updates: Partial<DesktopNotificationPrefs>) => {
    setState(prev => {
      const newPrefs = { ...prev.preferences, ...updates };

      // Save to localStorage
      try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(newPrefs));
      } catch (e) {
        // Ignore localStorage errors
      }

      return { ...prev, preferences: newPrefs };
    });
  }, []);

  // Check if should prompt for permission (never asked + not denied)
  const shouldPromptForPermission = state.isSupported && state.permission === 'default';

  // Check if notifications are fully enabled
  const isEnabled = state.isSupported && state.permission === 'granted' && state.preferences.enabled;

  return {
    // State
    permission: state.permission,
    isSupported: state.isSupported,
    serviceWorkerReady: state.serviceWorkerReady,
    preferences: state.preferences,
    shouldPromptForPermission,
    isEnabled,
    isQuietHours: isQuietHours(),

    // Actions
    requestPermission,
    showNotification,
    updatePreferences,

    // Convenience methods
    showHuddleInvite,
    showClientMessage,
    showTeamMessage,
    showMention,
    showTaskAssigned,
  };
}
